import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { giftCardDesignApi } from '../../api';
import {
  IGiftCardDesign,
  IGiftCardDesignListParams,
  ICreateGiftCardDesignPayload,
  IUpdateGiftCardDesignPayload,
} from '../../types';

export interface GiftCardDesignState {
  loading: boolean;
  designs: IGiftCardDesign[];
  selectedDesign: IGiftCardDesign | null;
  pagination: {
    totalPages: number;
    currentPage: number;
    totalRecords: number;
    hasNext: boolean;
  } | null;
  error: string;
}

const initialState: GiftCardDesignState = {
  loading: false,
  designs: [],
  selectedDesign: null,
  pagination: null,
  error: '',
};

export const giftCardDesignSlice = createSlice({
  name: 'giftCardDesign',
  initialState,
  reducers: {
    clearSelectedDesign: (state) => {
      state.selectedDesign = null;
    },
    clearError: (state) => {
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    // Fetch gift card designs list
    builder.addCase(fetchGiftCardDesigns.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    builder.addCase(fetchGiftCardDesigns.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.designs = payload.data;
      state.pagination = payload.pagination;
    });
    builder.addCase(fetchGiftCardDesigns.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });

    // Fetch single gift card design
    builder.addCase(fetchGiftCardDesign.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    builder.addCase(fetchGiftCardDesign.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.selectedDesign = payload;
    });
    builder.addCase(fetchGiftCardDesign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });

    // Create gift card design
    builder.addCase(createGiftCardDesign.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    builder.addCase(createGiftCardDesign.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.designs = [payload, ...state.designs];
    });
    builder.addCase(createGiftCardDesign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });

    // Update gift card design
    builder.addCase(updateGiftCardDesign.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    builder.addCase(updateGiftCardDesign.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.designs = state.designs.map((design) =>
        design.id === payload.id ? payload : design
      );
      if (state.selectedDesign?.id === payload.id) {
        state.selectedDesign = payload;
      }
    });
    builder.addCase(updateGiftCardDesign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });
  },
});

// Async Thunks
export const fetchGiftCardDesigns = createAsyncThunk(
  'giftCardDesign/fetchGiftCardDesigns',
  async (params?: IGiftCardDesignListParams) => {
    try {
      const { data } = await giftCardDesignApi.listGiftCardDesigns(params);
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const fetchGiftCardDesign = createAsyncThunk(
  'giftCardDesign/fetchGiftCardDesign',
  async (id: string) => {
    try {
      const { data } = await giftCardDesignApi.getGiftCardDesign(id);
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const createGiftCardDesign = createAsyncThunk(
  'giftCardDesign/createGiftCardDesign',
  async (payload: ICreateGiftCardDesignPayload, { dispatch }) => {
    try {
      const { data } = await giftCardDesignApi.createGiftCardDesign(payload);
      // Optionally refresh the list after creation
      // dispatch(fetchGiftCardDesigns());
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const updateGiftCardDesign = createAsyncThunk(
  'giftCardDesign/updateGiftCardDesign',
  async (
    params: { id: string; payload: IUpdateGiftCardDesignPayload },
    { dispatch }
  ) => {
    try {
      const { id, payload } = params;
      const { data } = await giftCardDesignApi.updateGiftCardDesign(
        id,
        payload
      );
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

// Selector
export const giftCardDesignSelector = (state: RootState) =>
  state.giftCardDesign;

// Actions
export const { clearSelectedDesign, clearError } = giftCardDesignSlice.actions;

export default giftCardDesignSlice.reducer;
